import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { ProductKeySchema } from '../product-key/product-key';
import {
  ENTITLEMENT_NOTE_MAX_LENGTH,
  UserEntitlementSchema,
} from '../user-entitlement/user-entitlement';
import { ProductDurationDaysSchema } from '../subscription-product/subscription-product';

/**
 * An admin grant with no money behind it (`store = 'promo'`) — App Review test
 * accounts, internal testers, comps.
 *
 * ## `note` is required, and that is the point
 *
 * Handing out commercial access for free is the one write in billing with no
 * paper trail behind it: there is no order, no receipt and no store to ask. The
 * note is what makes it auditable as a *decision* rather than as a row, and it
 * is the difference between "why does this account have premium?" answerable in
 * seconds and answerable never. Every other field is recoverable from the row;
 * this one exists only if somebody wrote it.
 *
 * ## `durationDays` may be null
 *
 * An open-ended comp — access until somebody revokes it. App Review needs one,
 * because a reviewer must not hit an expiry mid-review, and no paid channel can
 * produce one. Omitted means "use the product's own `durationDays`", which is
 * what an ordinary internal tester wants.
 */
export const PromoGrantCreateSchema = z
  .object({
    userId: UuidSchema,
    productKey: ProductKeySchema,
    /** `null` = open-ended; omitted = the product's own duration. */
    durationDays: ProductDurationDaysSchema.nullable().optional(),
    note: z.string().trim().min(1).max(ENTITLEMENT_NOTE_MAX_LENGTH),
  })
  .strict();

export type PromoGrantCreateRequest = z.infer<typeof PromoGrantCreateSchema>;

/**
 * Taking an entitlement back.
 *
 * Applies to any store, not only `promo`: a refunded IAP arrives here through
 * the webhook and a mistaken direct order through P23a3, and all three want the
 * same terminal write. The reason is required for the same argument as the
 * grant's note — this one removes something a member may have paid for.
 */
export const EntitlementRevokeSchema = z
  .object({
    reason: z.string().trim().min(1).max(ENTITLEMENT_NOTE_MAX_LENGTH),
  })
  .strict();

export type EntitlementRevokeRequest = z.infer<typeof EntitlementRevokeSchema>;

/** The admin entitlement viewer's rows — every entitlement one member holds. */
export const MemberEntitlementsResponseSchema = z
  .object({
    entitlements: z.array(UserEntitlementSchema),
  })
  .strict();

export type MemberEntitlementsResponse = z.infer<typeof MemberEntitlementsResponseSchema>;
